'use client';

/**
 * Modernised remake of the legacy LMS's CourseCardBorderless: a
 * frameless, fully-rounded tile — cover with a heart/watch-later
 * action pair that only surfaces on hover, a two-line title, and a
 * seamless footer holding the CTA. Unlike the legacy version (a fixed
 * pixel width for its own carousel), this fills its grid cell like
 * every other variant here and drives its corner radius, aspect
 * ratio, and CTA color from the same theme tokens the rest of the
 * package uses instead of hand-picked hexes.
 *
 * <p>Favorite / watch-later wiring mirrors {@code
 * ClassicNetflixZoomCourseCard}'s {@code useFavoriteToggle}/{@code
 * useWatchLaterToggle} hooks — same {@code UserResourceSource}
 * optimistic-toggle/error-rollback shape, same toast copy.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark, BookmarkCheck, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { PortalLink, useCurrentUser, useUserResourceSource } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { CoursePluginIcon } from '../_shared/pluginIcons';
import { Button, Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';
import { courseCtaLabel } from '../_shared/courseCtaLabel';
import { isLiveCourse, isLiveNow } from '../_shared/courseLive';
import { useQuickStart } from '../_shared/useQuickStart';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ButtonRadius, ProgressTextPlace } from './registry';

const BORDERLESS_CARD_CSS = `
.lms-borderless-card {
  border-top-left-radius: var(--radius-xl, 20px);
  border-top-right-radius: var(--radius-xl, 20px);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  transition: transform 200ms ease, box-shadow 200ms ease;
  box-shadow: var(--shadow-sm, 0 5px 10px rgba(0,0,0,0.14));
}
.lms-borderless-card:hover,
.lms-borderless-card:focus-within {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md, 0 14px 28px rgba(0,0,0,0.14));
}
.lms-borderless-card-action {
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 160ms ease, transform 160ms ease, background 160ms ease, color 160ms ease;
}
.lms-borderless-card:hover .lms-borderless-card-action,
.lms-borderless-card:focus-within .lms-borderless-card-action {
  opacity: 1;
  transform: translateY(0);
}
.lms-borderless-card-action[aria-pressed="true"] {
  background: var(--color-brand-500, #3b5bdb) !important;
  border-color: var(--color-brand-500, #3b5bdb) !important;
  color: var(--color-text-on-brand, #fff) !important;
}
@media (prefers-reduced-motion: reduce) {
  .lms-borderless-card,
  .lms-borderless-card-action { transition: none; }
}
.lms-borderless-live-time {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
}
.lms-borderless-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid var(--color-danger-500, #ef4444);
  color: var(--color-danger-500, #ef4444);
  flex-shrink: 0;
}
.lms-borderless-live-badge[data-muted="true"] {
  border-color: var(--color-border-default, #d0d0d0);
  color: var(--color-text-tertiary, #6a6f73);
}
.lms-borderless-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.lms-borderless-live-badge:not([data-muted="true"]) .lms-borderless-live-dot {
  animation: lms-borderless-live-pulse 1.4s ease-in-out infinite;
}
@keyframes lms-borderless-live-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
@media (prefers-reduced-motion: reduce) {
  .lms-borderless-live-badge:not([data-muted="true"]) .lms-borderless-live-dot { animation: none; }
}
`;

/** {@code HH:mm}, local time — same shape as the legacy card's dayjs
 *  format, no dayjs dependency needed for one field. */
function formatTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Same optimistic-toggle shape as {@code ClassicNetflixZoomCourseCard}'s
 *  identically-named hook — see that file for the full rationale. */
function useFavoriteToggle(courseSlug: string, courseTitle: string) {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.wishlist) return null;
      return (await ur.wishlist.has(courseSlug, signal)) ?? false;
    },
    [user, ur, courseSlug],
  );

  const available = Boolean(user) && Boolean(ur?.wishlist);
  const favorited = optimistic ?? (state.kind === 'loaded' ? state.data : false);

  const toggle = async () => {
    if (!available || toggling) return;
    setToggling(true);
    setOptimistic(!favorited);
    try {
      const res = await ur!.wishlist!.toggle({ courseSlug });
      if (!res || res.skipped) {
        setOptimistic(null);
      } else {
        const active = Boolean(res.active);
        setOptimistic(active);
        toast.success(
          t(active ? 'message.addedToFavoriteCourseList' : 'message.removedFromFavoriteCourseList'),
          { description: courseTitle },
        );
      }
    } catch (err) {
      setOptimistic(null);
      console.error('[BorderlessCourseCard] wishlist toggle failed', err);
      toast.error(t('label.error', 'Failed — try again'));
    } finally {
      setToggling(false);
    }
  };

  return { available, favorited, toggling, toggle };
}

/** Same optimistic-toggle shape as {@code ClassicNetflixZoomCourseCard}'s
 *  identically-named hook — see that file for the full rationale. */
function useWatchLaterToggle(courseSlug: string, courseTitle: string) {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.courseWatchLater) return null;
      return (await ur.courseWatchLater.has(courseSlug, signal)) ?? false;
    },
    [user, ur, courseSlug],
  );

  const available = Boolean(user) && Boolean(ur?.courseWatchLater);
  const active = optimistic ?? (state.kind === 'loaded' ? state.data : false);

  const toggle = async () => {
    if (!available || toggling) return;
    setToggling(true);
    setOptimistic(!active);
    try {
      const res = await ur!.courseWatchLater!.toggle({ courseSlug });
      if (!res || res.skipped) {
        setOptimistic(null);
      } else {
        const next = Boolean(res.active);
        setOptimistic(next);
        toast.success(
          t(next ? 'message.addedToWatchLaterList' : 'message.removedFromWatchLaterList'),
          { description: courseTitle },
        );
      }
    } catch (err) {
      setOptimistic(null);
      console.error('[BorderlessCourseCard] watch-later toggle failed', err);
      toast.error(t('label.error', 'Failed — try again'));
    } finally {
      setToggling(false);
    }
  };

  return { available, active, toggling, toggle };
}

export function BorderlessCourseCard({
  course: c,
  buttonRadius = 'pill',
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  /** CTA button corner radius. Defaults to a full pill, matching the
   *  legacy card's rounded footer button. */
  buttonRadius?: ButtonRadius;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t } = useTranslation('common');
  const favorite = useFavoriteToggle(c.id, c.title);
  const watchLater = useWatchLaterToggle(c.id, c.title);
  const hasProgress = showProgress && typeof c.progress === 'number'; // && c.progress > 0;
  const quickStart = useQuickStart(c, t);
  // Attendee status decides the label first; Quick Start only overrides
  // the "not started yet" case with "Start Now" — "Continue Learning"
  // (already in progress) stays as-is since Quick Start would otherwise
  // read as "restart". Approval-required courses always keep "Request"
  // and route through the detail page, same as when Quick Start is off.
  const baseCtaLabel = courseCtaLabel(t, c.attendeeStatus, c.approvalRequired);
  const ctaLabel =
    quickStart.available && !c.approvalRequired && baseCtaLabel === t('label.startLearning')
      ? t('label.startNow')
      : baseCtaLabel;
  const live = isLiveCourse(c);
  const liveNow = live && isLiveNow(c);

  return (
    <Card
      className="lms-borderless-card"
      style={{
        ...cardReset,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: 'var(--color-surface-card, #fff)',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: BORDERLESS_CARD_CSS }} />

      {/* Cover — a plain relative wrapper, NOT itself the link: the
          favorite/watch-later buttons need to sit outside any anchor (a
          <button> nested in an <a> is invalid HTML), so the cover image
          is its own PortalLink and the actions are its absolutely
          positioned siblings. */}
      <div style={{ position: 'relative' }}>
        <PortalLink href={c.href} style={{ display: 'block' }}>
          <div
            style={{
              aspectRatio: 'var(--app-course-image-aspect-ratio, 1.6)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <CourseCoverImage
              image={typeof c.image === 'string' ? c.image : undefined}
              resizeTo="480x0"
            />
          </div>
        </PortalLink>

        {/* Session time lives on the cover, not the footer — the footer's
            right slot is either a CTA button (live now) or nothing (still
            scheduled), so a time string sharing that slot would visually
            swap places with the button between states. */}
        {live && (
          <span className="lms-borderless-live-time">
            {formatTime(c.liveStartDate)} - {formatTime(c.liveEndDate)}
          </span>
        )}

        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <Button
            type="button"
            variant="outline"
            size="icon"
            radius="pill"
            className="lms-borderless-card-action"
            title={t('title.favorites')}
            aria-label={t('title.favorites')}
            aria-pressed={favorite.favorited}
            disabled={!favorite.available || favorite.toggling}
            onClick={(e) => {
              e.preventDefault();
              void favorite.toggle();
            }}
            style={{
              background: 'rgba(255,255,255,0.9)',
              borderColor: 'var(--color-border-subtle, rgba(0,0,0,0.15))',
              backdropFilter: 'blur(4px)',
              color: '#1c1d1f',
              width: 30,
              height: 30,
            }}
          >
            <Heart size={15} fill={favorite.favorited ? 'currentColor' : 'none'} />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            radius="pill"
            className="lms-borderless-card-action"
            title={t('label.watchLater')}
            aria-label={t('label.watchLater')}
            aria-pressed={watchLater.active}
            disabled={!watchLater.available || watchLater.toggling}
            onClick={(e) => {
              e.preventDefault();
              void watchLater.toggle();
            }}
            style={{
              background: 'rgba(255,255,255,0.9)',
              borderColor: 'var(--color-border-subtle, rgba(0,0,0,0.15))',
              backdropFilter: 'blur(4px)',
              color: '#1c1d1f',
              width: 30,
              height: 30,
            }}
          >
            {watchLater.active ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
          </Button>
        </div>
      </div>

      <PortalLink
        href={c.href}
        style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
      >
        <h3
          style={{
            margin: 0,
            padding: '10px 10px 4px',
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 600,
            lineHeight: 1.5,
            minHeight: 'calc(1.35em * 2)',
            color: 'var(--color-text-primary, #1c1d1f)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            height: '57px',
          }}
        >
          {c.title}
        </h3>

        {hasProgress && (
          <div style={{ padding: '10px 0 0' }}>
            <CourseProgressBar
              progress={c.progress!}
              showText={showProgressText}
              textPlace={progressTextPlace}
              radius={0}
            />
          </div>
        )}
      </PortalLink>

      {/* Footer — inset top-shadow + CTA + content-type glyph, matching
          the legacy card's recessed footer strip. No marginTop: auto — it
          used to push the footer to the bottom of the (grid-stretched)
          card, leaving a gap above it whenever a card had no progress bar
          or a 1-line title. The footer now sits directly after its
          preceding content, flush with the progress bar. */}
      <div
        style={{
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          background: 'var(--color-surface-muted, rgba(120,120,140,0.04))',
          boxShadow: 'inset 0 5px 3px -4px rgba(0,0,0,0.1)',
          borderBottomLeftRadius: 'var(--radius-none, 0px)',
          borderBottomRightRadius: 'var(--radius-none, 0px)',
        }}
      >
        {live ? (
          <>
            {/* Live: no CTA until the session window opens — the LIVE badge
                (below) is the only affordance while it's still scheduled.
                Once `liveNow`, "Start Now" either opens the external meeting
                link directly or, lacking one, registers then jumps to the
                in-portal player — same as the non-live Quick Start flow. */}
            {liveNow && c.publicJoinUrl ? (
              <Button
                type="button"
                variant="default"
                size="xs"
                radius={buttonRadius}
                style={{ width: 'auto' }}
                onClick={() => window.open(c.publicJoinUrl, '_blank', 'noopener,noreferrer')}
              >
                {t('label.startNow')}
              </Button>
            ) : liveNow ? (
              <Button
                type="button"
                variant="default"
                size="xs"
                radius={buttonRadius}
                style={{ width: 'auto' }}
                disabled={!quickStart.available || quickStart.loading}
                onClick={quickStart.start}
              >
                {t('label.startNow')}
              </Button>
            ) : (
              // Keeps the live badge pinned right via space-between —
              // the time itself now lives on the cover image.
              <span />
            )}
            <span className="lms-borderless-live-badge" data-muted={liveNow ? undefined : 'true'}>
              <span className="lms-borderless-live-dot" />
              {t('label.live')}
            </span>
          </>
        ) : (
          <>
            <Button
              asChild
              variant="default"
              size="xs"
              radius={buttonRadius}
              style={{ width: 'auto' }}
              disabled={quickStart.loading}
            >
              <PortalLink
                href={c.href}
                onClick={!c.approvalRequired ? quickStart.start : undefined}
              >
                {ctaLabel}
              </PortalLink>
            </Button>
            <CoursePluginIcon
              pluginId={c.pluginId}
              domain={c.domain}
              size={22}
              style={{ flexShrink: 0 }}
            />
          </>
        )}
      </div>
    </Card>
  );
}

BorderlessCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 200,
  gap: 'clamp(12px, 2vw, 16px)',
} satisfies CardGridSpec;
